import React, { memo, useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useMatch } from "react-router-dom"
import { DateTime } from "luxon"

import { Box, Text } from "@mos-cat/ds"
import { usePageContext } from "@components/contexts/PageContext"
import { useZoneContext } from "@components/contexts/ZoneContext"
import { EditorPreview, stringToEditorContent } from "@components/form"
import {
  PageBlockRow,
  PageBlockRowSection,
  PageBlockContent,
  PageBlockHeading,
  // StarIcon,
  getFormattedRecentDate,
  Button,
  Favorites,
  Subscription,
  useAppContext,
  CircleCrossIcon,
  CustomGetUsersOptionLabel,
  Avatar,
  Ellipsis,
  Tooltip,
} from "@dit/core-frontend"

import { PageActions } from "@components/pages/PageActions"
import { PageStatus } from "@components/pages/PageStatus"
import { CommentsBlock } from "../Comment/CommentsBlock"
import { Edit, Warning } from "@mos-cat/ds-icons"

import { API_ENDPOINTS, ROUTES } from "@constants"
import { PAGE_STATUSES } from "@utils/pages"
import { ENTITY_TYPES } from "@utils/favorites"

import styles from "./pagePreview.module.css"
import { ZONE_SECTION_LINKS } from "@utils/zones"
import TextCommentsButton from "../general/TextCommentsButton"
import { useWebSocket } from "@src/hooks/useWebSocket"
import cc from "classcat"

export const PagePreview = memo(() => {
  const { page, setPage } = usePageContext()
  const { isMobileView, sseLink, user } = useAppContext()
  const [updateMessage, setUpdateMessage] = useState(null)
  const [scrollHeight, setScrollHeight] = useState(0)
  const [isPanelShow, setIsPanelShow] = useState(true)
  const isPageArchive = page.archive

  const handleMessage = useCallback(async (message) => {
    const data = JSON.parse(atob(message.data.replace(/"/g, ""))) || null
    if (data?.sender?.id !== user?.id && data?.text) {
      setUpdateMessage(data)
    }
  }, [])
  const [status] = useWebSocket(`${sseLink}?topics[]=page.${page?.id}`, handleMessage)
  //ws://localhost:8080/sse для локального теста
  const { zone, deletePage, checkZonePermission, togleTextCommentsShow, getZone, updatePage } =
    useZoneContext()
  const navigate = useNavigate()
  const isInPages = !!useMatch(`/${ROUTES.ZONE}/:zoneSlug/${ZONE_SECTION_LINKS.PAGES}/*`)

  const isDraft = page.status.slug === PAGE_STATUSES.DRAFT

  const handleNavigation = useCallback(
    (e) => {
      const window = e.target
      if (window.scrollTop > 100) {
        if (scrollHeight > window.scrollTop) {
          setIsPanelShow(true)
        } else if (scrollHeight < window.scrollTop) {
          setIsPanelShow(false)
        }
        setScrollHeight(window.scrollTop)
      }
    },
    [scrollHeight],
  )

  useEffect(() => {
    const scrollElem = document.getElementById("mainContainer")
    setScrollHeight(scrollElem.scrollTop)
    scrollElem.addEventListener("scroll", handleNavigation)

    return () => {
      scrollElem.removeEventListener("scroll", handleNavigation)
    }
  }, [handleNavigation])

  return (
    <>
      <PageBlockRow
        place="middle"
        position="sticky"
        top="-40px"
        zIndex="10"
        alignItems="center"
        backgroundColor="white"
        m={["0 -10px!important", "0 -20px!important", "0 -20px 0 -40px!important"]}
        p={["10px", "20px", "10px 20px 10px 40px"]}
        width="auto"
        borderBottom="1px solid #EEEFF2"
        display="flex"
        flexDirection={["column-reverse", "row"]}
        flexWrap={["nowrap", "nowrap", "nowrap", "wrap"]}
        className={cc({
          [styles.topPanel]: true,
          [styles.show]: isPanelShow,
          [styles.hide]: !isPanelShow,
        })}
      >
        <PageBlockRowSection
          overflow="hidden"
          flexDirection={["column-reverse", "column"]}
          alignItems="flex-start"
          gridGap="10px"
          width={["100%", "auto", "auto"]}
        >
          <Box
            display="flex"
            alignItems={["flex-start", "center"]}
            gridGap="10px"
            justifyContent="space-between"
            width={["100%", "auto", "auto"]}
          >
            <Box display="flex" alignItems="center" gridGap="5px">
              <Avatar size={20} url={(page.lastModifiedBy || page.user)?.photo?.url} />
              <Text fontSize="14px" color="gray-80">
                {CustomGetUsersOptionLabel(page.lastModifiedBy || page.user)}
              </Text>
            </Box>
            <Text fontSize="14px" color="gray-40" styles={{ whiteSpace: "nowrap" }}>
              {getFormattedRecentDate(DateTime.fromISO(page.modifiedAt))}
            </Text>
            {!isDraft && (
              <Text
                as={Box}
                backgroundColor="gray-10"
                p="5px 10px"
                color="gray-60"
                fontSize="14px"
                borderRadius="4px"
                styles={{ whiteSpace: "nowrap" }}
              >
                Версия страницы : {page.version}
              </Text>
            )}
            {updateMessage && (
              <Box
                width="fit-content"
                display="flex"
                alignItems="center"
                gridGap="10px"
                padding="5px 10px"
                backgroundColor="#FFF5E8"
                border="1px solid rgba(255, 245, 232, 1)"
              >
                <Text>
                  <Warning />{" "}
                </Text>
                <Text fontSize="14px">
                  <Link to={`/${ROUTES.PROFILE}/${updateMessage?.sender?.id}`}>
                    {CustomGetUsersOptionLabel(updateMessage?.sender)}
                  </Link>
                </Text>

                <Text fontSize="14px" color="gray-80">
                  {updateMessage?.text}
                </Text>
                {/* <Text fontSize="14px" color="gray-80">
                  {updateMessage.type === "wiki_page_update"
                    ? "опубликовал(а) новую версию страницы. Обновите страницу."
                    : "оставил (а) комментарий к этой странице. Обновите страницу"}
                </Text> */}
              </Box>
            )}
          </Box>
        </PageBlockRowSection>
        <Box
          display="flex"
          flexWrap="wrap"
          gridGap={["5px", "15px", "15px", "20px"]}
          width={["100%", "auto", "auto"]}
          justifyContent={["flex-end", "flex-end", "flex-end", "space-between"]}
        >
          <Box display="flex" alignItems="center" gridGap="5px">
            <PageStatus status={page.status} />
          </Box>
          <Box display="flex" alignItems="center" gridGap="7px">
            {!isPageArchive && checkZonePermission("PAGE_EDIT") && (
              <>
                {/* Переход на относительный путь */}
                <Tooltip content="Редактировать" disabled={isMobileView}>
                  <Link to="edit">
                    <Button kind="gray" padding="5px!important">
                      <Edit />
                    </Button>
                  </Link>
                </Tooltip>
              </>
            )}
            <Subscription
              itemId={page.id}
              subscriptionId={page.subscription?.id}
              type="page"
              requestUrl={API_ENDPOINTS.subscription}
              iconSize={21}
              p="5px !important"
              kind="gray"
              isButtonView
            />
            <Favorites
              itemId={page.id}
              favoritesId={page.favorites?.id}
              type={ENTITY_TYPES.PAGE}
              requestUrl={API_ENDPOINTS.favorites}
              iconSize={21}
              p="5px !important"
              kind="gray"
              isButtonView
            />
            <TextCommentsButton
              togleTextCommentsShow={togleTextCommentsShow}
              kind="gray"
              p="5px !important"
            />
            <PageActions
              page={page}
              as={Button}
              kind="gray"
              p="0 !important"
              onDelete={() => {
                deletePage(page.id)
                navigate(
                  isInPages ? `${ROUTES.ZONE}/${zone.slug}/pages` : `${ROUTES.ZONE}/${zone.slug}`,
                  { replace: true },
                )
                if (page?.id === zone?.homePage) {
                  getZone()
                }
              }}
              onUpdate={(pageId, _, data) => {
                setPage((prevPageData) => ({ ...prevPageData, ...data }))
                updatePage(pageId, data)
              }}
            />
            {isInPages && (
              <Tooltip content="Закрыть" disabled={isMobileView}>
                <Link to={`${ROUTES.ZONE}/${zone.slug}/pages`}>
                  <Button kind="gray" padding="5px!important">
                    <CircleCrossIcon size={22} />
                  </Button>
                </Link>
              </Tooltip>
            )}
          </Box>
          {/* TODO Команда */}
          {/*<Box>*/}
          {/*  <Button {...grayButtonProps}>*/}
          {/*    <Text as={Box} px="16px" color="gray-60" fontSize="14px">*/}
          {/*      Подписаться*/}
          {/*    </Text>*/}
          {/*  </Button>*/}
          {/*</Box>*/}
        </Box>
      </PageBlockRow>
      <PageBlockContent>
        <PageBlockHeading styles={{ maxWidth: "100%", marginBottom: "20px" }}>
          <Text
            fontSize={["24px", "34px", "44px"]}
            lineHeight={["28px", "38px", "48px"]}
            className={styles.title}
          >
            <Ellipsis lines={1}>{page?.title}</Ellipsis>
          </Text>
        </PageBlockHeading>
        {checkZonePermission("PAGE_READ") && (
          <EditorPreview
            content={stringToEditorContent(page.text || page.plainText)}
            addComments
            isPageArchive={isPageArchive}
          />
        )}
        <CommentsBlock page={page} stackClassName={styles.bodyBlock} />
      </PageBlockContent>
    </>
  )
})

PagePreview.displayName = "PagePreview"
